import { h } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { useRecsProvider } from '../search/useSearchProvider';
import { MiniCartProductCard, MiniCartProductCardSkeleton } from '../components/product/MiniCartProductCard';
import { readBoostRecsOptionsFromDataset } from '../platforms/boost/api';
import { detectPageType } from '../platforms/boost/analytics/utils';
import { TrackingProvider } from '../analytics/context';
import styles from './MiniCartRecommendationWidget.module.scss';
// Boost recsTypes that require a seed product id to return meaningful results.
// Calling the API for one of these without a seed yields ~12 generic products
// (we observed this empirically) and wastes a round-trip — so when the widget
// boots with no seed (typically because the cart was empty at page load) we
// skip the first-open fetch for these types and wait for the retargeter to
// supply a real seed via the cart-update signal.
const SEED_REQUIRED_RECS_TYPES = new Set([
    'related-items',
    'frequently-bought-together',
]);
function ChevronIcon() {
    return (h("svg", { viewBox: "0 0 12 12", "aria-hidden": "true" },
        h("path", { d: "M2 4.5 L6 8 L10 4.5", fill: "none", stroke: "currentColor", "stroke-width": "1.5", "stroke-linecap": "round", "stroke-linejoin": "round" })));
}
export function MiniCartRecommendationWidget({ isVisible, layout = 'side-panel', title, skeletonCount = 4, providerType, mockPlatform, seedProductId, recsType, recsOptions, mountElement, }) {
    var _a;
    const options = Object.assign(Object.assign({}, readBoostRecsOptionsFromDataset(mountElement)), recsOptions);
    // `recsType` is required downstream (see `RecsConfig`); coerce missing values
    // to `''` so the hook's own short-circuit applies.
    //
    // `awaitRefresh: true` is load-bearing — do NOT flip to false. The seed
    // prop is pinned at bootstrap; cart-driven retargeting goes through
    // `__refreshRecs(override)`. With `awaitRefresh: false`, `useRecsProvider`'s
    // auto-fetch would refire on `recsConfigKey` changes, call `loadRecs()`
    // with no override, and overwrite retargeted products with the stale
    // bootstrap seed (and AbortController-cancel the in-flight retarget on
    // the way out).
    const { loading, products, meta, loadRecs } = useRecsProvider(providerType, mockPlatform, Object.assign(Object.assign({ seedProductId, recsType: recsType !== null && recsType !== void 0 ? recsType : '' }, options), { awaitRefresh: true }));
    // Mount a TrackingProvider so the quick-add from each recommended tile fires
    // Boost's add_to_cart with itemListId 'recommendations' and the recs rid
    // (correlationId). MiniCartProductCard reads this context — without it its
    // add-to-cart call is a no-op (see the `if (!tracking) return` guard there).
    const trackingValue = useMemo(() => {
        var _a, _b;
        return ({
            itemListId: 'recommendations',
            pageType: (_a = options.pageType) !== null && _a !== void 0 ? _a : detectPageType(),
            correlationId: (_b = meta === null || meta === void 0 ? void 0 : meta.analytics) === null || _b === void 0 ? void 0 : _b.queryID,
            widgetId: options.widgetId,
        });
    }, [options.pageType, options.widgetId, (_a = meta === null || meta === void 0 ? void 0 : meta.analytics) === null || _a === void 0 ? void 0 : _a.queryID]);
    // First-open fetch — fires once per component lifetime. Ref isn't reset on
    // `isVisible: false`, so a drawer reopen after a retargeting cart-update
    // doesn't re-run `loadRecs()` (which would use the stale prop seed and
    // abort the retarget mid-flight). Every refetch after this is driven by
    // `__refreshRecs(override)` from the cart:updated retargeter.
    //
    // Skip when seed-based but no seed bootstrapped (empty cart at page load) —
    // otherwise we'd burn a Boost POST with seed='' and race the retargeter.
    const hasFetchedRef = useRef(false);
    useEffect(() => {
        if (!isVisible)
            return;
        if (hasFetchedRef.current)
            return;
        if (SEED_REQUIRED_RECS_TYPES.has(recsType !== null && recsType !== void 0 ? recsType : '') && !seedProductId)
            return;
        hasFetchedRef.current = true;
        loadRecs();
    }, [isVisible, loadRecs, recsType, seedProductId]);
    // Refresh-lifecycle wiring — same contract as `ProductRecommendationSection`.
    // `__refreshRecs(override?)` is the internal entry point invoked by
    // `window.mrRecommendations.refresh(selector, override?)`; `mr:recs:ready`
    // (and `mr:recs:unready` on cleanup) lets queued early-refresh callers
    // resolve once the handler is wired.
    //
    // The handler claims the first-fetch slot (`hasFetchedRef.current = true`)
    // before delegating, so a retargeter call that lands before the drawer's
    // ever been opened can't be undone by the subsequent first-open effect
    // (which would otherwise fire `loadRecs()` with the stale prop seed).
    useEffect(() => {
        if (!mountElement)
            return;
        const refreshHandler = (override) => {
            hasFetchedRef.current = true;
            return loadRecs(override);
        };
        mountElement.__refreshRecs = refreshHandler;
        mountElement.dispatchEvent(new CustomEvent('mr:recs:ready', { bubbles: true }));
        return () => {
            delete mountElement.__refreshRecs;
            mountElement.dispatchEvent(new CustomEvent('mr:recs:unready', { bubbles: true }));
        };
    }, [mountElement, loadRecs]);
    // Mobile-only: collapse toggle (chevron on the header). Default expanded;
    // state survives resize-driven layout changes so users don't lose their place.
    const [collapsed, setCollapsed] = useState(false);
    // Mobile-only: dot paginator. One dot per tile; `activeDot` indexes whichever
    // tile is currently snapped closest to the scroll container's leading edge.
    // Tracked via a scroll listener — cheap, no observers needed. We re-attach
    // after the layout switches (portal host change) or the product list
    // changes, so stale element references are never kept.
    const listRef = useRef(null);
    const [activeDot, setActiveDot] = useState(0);
    useEffect(() => {
        if (layout !== 'accordion-carousel')
            return;
        const el = listRef.current;
        if (!el)
            return;
        const update = () => {
            const max = el.scrollWidth - el.clientWidth;
            if (max <= 0 || products.length === 0) {
                setActiveDot(0);
                return;
            }
            const fraction = Math.min(1, Math.max(0, el.scrollLeft / max));
            setActiveDot(Math.round(fraction * (products.length - 1)));
        };
        update();
        el.addEventListener('scroll', update, { passive: true });
        window.addEventListener('resize', update);
        return () => {
            el.removeEventListener('scroll', update);
            window.removeEventListener('resize', update);
        };
    }, [layout, products.length]);
    // Render: skeletons during the initial fetch, products once loaded, `null`
    // on closed/empty. Refreshes keep stale products visible (the
    // AbortController prevents stale-write races, so no flicker to skeletons).
    // Cart-empty visibility is gated by host CSS, not here.
    if (!isVisible)
        return null;
    if (!loading && products.length === 0)
        return null;
    return (h(TrackingProvider, { value: trackingValue },
        h("section", { class: styles.widget, "data-layout": layout, "aria-label": title || 'Recommendations' },
            h("header", { class: styles.header },
                title && h("h2", { class: styles.title }, title),
                h("button", { type: "button", class: `${styles.collapseToggle} ${collapsed ? styles.collapseToggleCollapsed : ''}`, onClick: () => setCollapsed(c => !c), "aria-expanded": !collapsed, "aria-label": collapsed ? 'Expand recommendations' : 'Collapse recommendations' },
                    h(ChevronIcon, null))),
            h("div", { class: `${styles.listWrap} ${collapsed ? styles.listWrapCollapsed : ''}` },
                h("div", { class: styles.list, ref: listRef }, loading && products.length === 0
                    ? Array.from({ length: skeletonCount }, (_, i) => (h(MiniCartProductCardSkeleton, { key: `skeleton-${i}` })))
                    : products.map(product => (h(MiniCartProductCard, { key: product.id, product: product })))),
                products.length > 1 && (h("div", { class: styles.paginator, "aria-hidden": "true" }, products.map((_, i) => (h("span", { key: i, class: `${styles.paginatorDot} ${i === activeDot ? styles.paginatorDotActive : ''}` })))))))));
}
